import { ChangeDetectionStrategy, Component, Injector } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { withHistory } from '@sama/rxjs-core';
import { interval } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { BrowserControlService } from '../../../../cross-team-shared/misc-services/browser-control.service';
import { EventTrackingService } from '../../../../shared/event-tracking/event-tracking.service';

interface ZoomRatios {
  normalizedPixelRatio: number;
  viewportScale: number;
}

@Component({
  selector: 'h3-browser-zoom-tracking',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BrowserZoomTrackingComponent extends DirectiveSuperclass {
  constructor(
    private browserControlService: BrowserControlService,
    private eventTrackingService: EventTrackingService,
    injector: Injector,
  ) {
    super(injector);
    this.subscribeTo(
      interval(1000).pipe(
        startWith(0),
        map(() => this.getCurrentZoomRatios()),
        filter(isDefined),
        withHistory(1),
        map((ratios) => this.getChangedZoomType(ratios)),
        filter(isDefined),
      ),
      this.trackEvent,
    );
  }

  private getCurrentZoomRatios(): ZoomRatios | undefined {
    // Ignore unless associated document is fully active
    // https://w3c.github.io/csswg-drafts/cssom-view/#dom-window-visualviewport
    if (!window.visualViewport) {
      return undefined;
    }

    const viewportScale = window.visualViewport.scale;
    if (this.browserControlService.isRetina()) {
      return {
        normalizedPixelRatio: window.devicePixelRatio / 2,
        viewportScale,
      };
    }
    return { normalizedPixelRatio: window.devicePixelRatio, viewportScale };
  }

  private getChangedZoomType([newZoomRatios, oldZoomRatios]: [
    ZoomRatios,
    (ZoomRatios | undefined)?,
  ]) {
    if (!oldZoomRatios) {
      return undefined;
    }
    if (
      oldZoomRatios.normalizedPixelRatio !== newZoomRatios.normalizedPixelRatio
    ) {
      return 'device_pixel_ratio';
    } else if (oldZoomRatios.viewportScale !== newZoomRatios.viewportScale) {
      return 'viewport_scale';
    } else {
      return undefined;
    }
  }

  private trackEvent(source: string) {
    const currentZoomRatios = this.getCurrentZoomRatios();
    assert(
      currentZoomRatios,
      "We don't track unless viewport is active and ratios are present",
    );

    this.eventTrackingService.track(
      {
        id: 'zoom',
        bucket: 'navigation',
        source,
        normalized_pixel_ratio: currentZoomRatios.normalizedPixelRatio,
        viewport_scale: currentZoomRatios.viewportScale,
      },
      { isInteractive: false },
    );
  }
}
